/** Just enough of a hast node (what react-markdown hands its components as `node`). */
export interface HastLike {
  type: string;
  tagName?: string;
  value?: string;
  children?: HastLike[];
}

const RTL = /[֐-ࣿיִ-﷿ﹰ-﻿]/g;
const LTR = /[A-Za-zÀ-ɏͰ-ϿЀ-ӿ]/g;
const URL = /\b(?:https?|file):\/\/\S+/gi;
// code is always English and a nested list decides its own direction, so neither votes for its parent
const SKIP = new Set(["code", "pre", "ul", "ol", "table"]);

function collect(node: HastLike, out: string[]): void {
  if (node.type === "text" && node.value) out.push(node.value);
  if (node.tagName && SKIP.has(node.tagName)) return;
  for (const c of node.children ?? []) collect(c, out);
}

/**
 * The direction of a block of mixed Arabic/English prose, by which script carries it rather than which comes first:
 * an Arabic line that opens with `typecheck` or **Codvero** is still Arabic. Code, URLs and nested lists don't count;
 * Arabic wins once it has at least half as many letters as English (Arabic words are shorter). Undefined when the
 * block has no letters at all, so it keeps the page's direction.
 */
export function blockDir(node: HastLike | undefined): "rtl" | "ltr" | undefined {
  if (!node) return undefined;
  const parts: string[] = [];
  // the block itself is read even when it is a table or a list; only what's nested inside it is skipped
  if (node.type === "text") collect(node, parts);
  else for (const c of node.children ?? []) collect(c, parts);
  const text = parts.join(" ").replace(URL, " ");
  const rtl = text.match(RTL)?.length ?? 0;
  const ltr = text.match(LTR)?.length ?? 0;
  if (!rtl && !ltr) return undefined;
  return rtl > 0 && rtl * 2 >= ltr ? "rtl" : "ltr";
}
